import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getSellerProfilesByIds } from "@/lib/seller-profiles";
import { queryKeys } from "@/lib/query-keys";

export interface SponsoredAsset {
  id: string;
  campaign_id: string;
  title: string;
  slug: string;
  price: number;
  is_free: boolean;
  pricing_type: string | null;
  pwyw_minimum: number | null;
  created_at: string;
  sale_price: number | null;
  sale_starts_at: string | null;
  sale_ends_at: string | null;
  category: {
    name: string;
    slug: string;
  } | null;
  seller: {
    id: string;
    display_name: string | null;
    username: string | null;
    avatar_url: string | null;
    is_seller: boolean;
    seller_verified: boolean;
    is_early_founder: boolean;
  };
  primary_image: string | null;
  rating: number;
  review_count: number;
}

interface UseSponsoredAssetsOptions {
  placement: "homepage" | "browse" | "search" | "category";
  categorySlug?: string | null;
  limit?: number;
  enabled?: boolean;
}

interface AssetImageRow {
  asset_id: string;
  image_url: string;
}

interface AssetReviewStatsRow {
  asset_id: string;
  avg_rating: number;
  review_count: number;
}

export function useSponsoredAssets({
  placement,
  categorySlug,
  limit = 3,
  enabled = true,
}: UseSponsoredAssetsOptions) {
  return useQuery({
    queryKey: queryKeys.assets.sponsored(placement, categorySlug ?? undefined, limit),
    queryFn: async (): Promise<SponsoredAsset[]> => {
      // Get sponsored asset IDs using the database function
      const { data: sponsoredData, error: sponsoredError } = await supabase.rpc(
        "get_sponsored_assets",
        {
          p_placement: placement,
          p_category_slug: categorySlug || undefined,
          p_limit: limit,
        }
      );

      if (sponsoredError) {
        console.error("Error fetching sponsored assets:", sponsoredError);
        return [] as any[];
      }

      if (!sponsoredData || sponsoredData.length === 0) {
        return [] as any[];
      }

      const assetIds = sponsoredData.map((s: { asset_id: string }) => s.asset_id);
      const campaignMap = new Map(
        sponsoredData.map((s: { asset_id: string; campaign_id: string }) => [
          s.asset_id,
          s.campaign_id,
        ])
      );

      // Fetch full asset details
      const { data: assets, error: assetsError } = await supabase
        .from("assets")
        .select(
          `
          id,
          title,
          slug,
          price,
          is_free,
          pricing_type,
          pwyw_minimum,
          created_at,
          seller_id,
          sale_price,
          sale_starts_at,
          sale_ends_at,
          category:categories(name, slug)
        `
        )
        .in("id", assetIds)
        .eq("status", "published")
        .eq("visibility", "public");

      if (assetsError) {
        console.error("Error fetching asset details:", assetsError);
        return [] as any[];
      }

      if (!assets) return [] as any[];

      const sellerIds = assets.map((asset) => asset.seller_id);

      const [imagesResult, reviewsResult, sellerMap] = await Promise.all([
        supabase
          .from("asset_images")
          .select("asset_id, image_url")
          .in("asset_id", assetIds)
          .eq("is_primary", true),
        // Use the aggregate RPC — returns one row per asset instead of all
        // individual review rows, cutting transfer from O(N_reviews) to O(N_assets).
        supabase.rpc("get_asset_review_stats", { p_asset_ids: assetIds }),
        getSellerProfilesByIds(sellerIds),
      ]);

      const imageMap = new Map<string, string>();
      imagesResult.data?.forEach((img: AssetImageRow) => {
        imageMap.set(img.asset_id, img.image_url);
      });

      const reviewStatsMap = new Map<string, AssetReviewStatsRow>();
      (reviewsResult.data ?? []).forEach((row: AssetReviewStatsRow) => {
        reviewStatsMap.set(row.asset_id, { ...row, avg_rating: Number(row.avg_rating) || 0 });
      });

      return assets.map((asset) => {
        const reviewStats = reviewStatsMap.get(asset.id) ?? { avg_rating: 0, review_count: 0, asset_id: asset.id };
        const avgRating = reviewStats.avg_rating;
        const sellerData = sellerMap.get(asset.seller_id || "");

        return {
          id: asset.id,
          campaign_id: campaignMap.get(asset.id) as string,
          title: asset.title,
          slug: asset.slug,
          price: Number(asset.price) || 0,
          is_free: asset.is_free || false,
          pricing_type: asset.pricing_type ?? null,
          pwyw_minimum: asset.pwyw_minimum != null ? Number(asset.pwyw_minimum) : null,
          created_at: asset.created_at,
          sale_price: asset.sale_price ? Number(asset.sale_price) : null,
          sale_starts_at: asset.sale_starts_at,
          sale_ends_at: asset.sale_ends_at,
          category: asset.category
            ? Array.isArray(asset.category)
              ? asset.category[0]
              : asset.category
            : null,
          seller: sellerData || {
            id: asset.seller_id || "",
            display_name: null,
            username: null,
            avatar_url: null,
            is_seller: false,
            seller_verified: false,
            is_early_founder: false,
          },
          primary_image: imageMap.get(asset.id) || null,
          rating: avgRating,
          review_count: reviewStats.review_count,
        };
      });
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
